// Spec 45 component-behavior — cmp_ds_button.
//
// 6 variants × 3 sizes × 6 states (default/hover/focus/pressed/disabled/
// loading) per spec 45 §behavior_matrices.cmp_ds_button. Variants:
// primary / secondary / tertiary / ghost / danger / link.
//
// Implementation choices:
//   * Native <button> per ARIA pattern (component-behavior#aria_button)
//     — `role='button'` only when a non-button element carries the
//     behavior, which we enforce by NOT exposing an `as` prop. asChild
//     via Radix Slot for the rare case where a Link / Anchor needs
//     button semantics; the Slot consumer is responsible for keyboard.
//   * `type='button'` default — prevents accidental form submission
//     per spec 45 aria_button.required_props_on_root.
//   * `aria-busy` while loading; spinner replaces label visually but
//     label stays in DOM (visually-hidden) so the accessible name
//     survives the loading state.
//   * Icon-only buttons go through IconButton component — that's the
//     surface that enforces the aria-label requirement.
//   * Style hooks via data-attributes (data-variant / data-size /
//     data-state) so consumer CSS can target without class name
//     coupling. Default styles come from the package; tenants can
//     re-skin via [data-variant=primary] selectors in their own CSS.
//   * Reduced motion: transform transitions suppressed; color/bg
//     transitions stay at motion_fast; opacity transitions stay.
//     Implemented in CSS via `@media (prefers-reduced-motion: reduce)`.

import { Slot } from "@radix-ui/react-slot";
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "ghost"
  | "danger"
  | "link";

export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  /** Visual + semantic variant. Defaults to 'primary'. */
  readonly variant?: ButtonVariant;
  /** Vertical size. Defaults to 'md'. */
  readonly size?: ButtonSize;
  /**
   * Loading state — spinner replaces label visually; label STAYS in
   * DOM (visually hidden) so the accessible name survives. aria-busy
   * is set; the button stays focusable but click is no-op'd by the
   * underlying type='button' (consumer should also disable via
   * disabled OR aria-disabled — see <ButtonProps#disabled> guidance).
   */
  readonly loading?: boolean;
  /**
   * Native HTML button type — defaults to 'button' so a <Button> inside
   * a form doesn't accidentally submit. Explicit 'submit' opts in.
   */
  readonly type?: "button" | "submit" | "reset";
  /**
   * Render the underlying element via Radix Slot. Caller passes the
   * desired element as the single child (e.g., <Link>). The Slot
   * forwards all button props onto it; caller is responsible for
   * keyboard semantics (Enter/Space activate).
   */
  readonly asChild?: boolean;
  readonly children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      loading = false,
      type = "button",
      asChild = false,
      disabled,
      className,
      children,
      onClickCapture,
      ...rest
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const effectiveDisabled = disabled === true || loading;

    return (
      <Comp
        ref={ref}
        type={asChild ? undefined : type}
        data-component="button"
        data-variant={variant}
        data-size={size}
        data-loading={loading || undefined}
        // Native disabled removes the element from the tab order. We
        // keep it focusable when loading (via aria-disabled) so the
        // user can read the loading announcement; native disabled
        // applies otherwise per spec 45 disabled_state_consistency.
        disabled={asChild ? undefined : disabled === true && !loading ? true : undefined}
        aria-disabled={effectiveDisabled || undefined}
        aria-busy={loading || undefined}
        className={cx("nai-btn", className)}
        {...rest}
        onClickCapture={(event) => {
          // aria-disabled preserves focus but does not suppress activation.
          // Capture also protects a slotted link's own bubbling click handler.
          if (effectiveDisabled) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          onClickCapture?.(event);
        }}
      >
        {children}
      </Comp>
    );
  },
);

Button.displayName = "Button";
